import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import PublicNavbar from "../components/PublicNavbar";
import Footer from "../components/Footer";
import Reveal, { StaggerReveal } from "../components/motion/Reveal";
import { FEATURE_PAGES, getFeaturePage } from "../data/featurePages";
import InspectionFilmstrip from "../components/InspectionFilmstrip";
import {
  ArrowRight, ArrowLeft, BookOpen, Gift, Quote, ChevronRight, ChevronDown,
  Sparkles, Check, XCircle, Plus, CalendarDays, ClipboardList, FileText,
  PenLine, CreditCard, Receipt, MailCheck, Globe,
} from "lucide-react";

const OVERLINE = "text-xs font-bold tracking-[0.2em] uppercase text-violet-600";

const CAPTURES = {
  "pilotage-calendrier": "/captures/statistiques.jpg",
  "assistant-ia": "/captures/copilot.jpg",
  "page-de-reservation": "/captures/site_reservation.jpg",
  "reservations-devis": "/captures/detail_reservation.jpg",
  "encaissements": "/captures/detail_reservation.jpg",
  "revenus-facturation": "/captures/statistiques.jpg",
};

const PORTRAITS = {
  "Marc D.": "https://static.prod-images.emergentagent.com/jobs/316a32c4-cbe9-4f91-b455-89f832706401/images/75e45bf202ffc88ea6aa8711f3d76e5e9405e816c10972d28e60082cd269ccbd.png",
  "Sonia B.": "https://static.prod-images.emergentagent.com/jobs/316a32c4-cbe9-4f91-b455-89f832706401/images/42adef955c15a662f1bb1f30e9b8b07a772aed60b429d091287c43f9a47802bb.png",
  "David P.": "https://static.prod-images.emergentagent.com/jobs/316a32c4-cbe9-4f91-b455-89f832706401/images/84a0a304f44dcd0e38b6d42112336c9b97118c62db3b125b118a5de118875861.png",
  "Nathalie R.": "https://static.prod-images.emergentagent.com/jobs/316a32c4-cbe9-4f91-b455-89f832706401/images/34ca6ab1459cbffe24089f2480e8bf481eca169aea30e285ad1aa863faa5190d.png",
  "Karim L.": "https://static.prod-images.emergentagent.com/jobs/316a32c4-cbe9-4f91-b455-89f832706401/images/387b6922e7c8fcb982f1953b104a3901555c9cd9fb6deb7f81c62f48829d1165.png",
  "Stéphane M.": "https://static.prod-images.emergentagent.com/jobs/316a32c4-cbe9-4f91-b455-89f832706401/images/fbe0bc5984e5cf8c91cf2fca965a46c6072ce65bf28a96d24e1e645ff649ddb0.png",
};

const STORIES = {
  "pilotage-calendrier": { pains: ["Le planning vit dans un tableur, les réservations au téléphone, les tâches dans la tête de chacun.", "Un double booking peut passer inaperçu jusqu'au matin du départ, devant le client.", "Chaque journée commence par une heure de vérifications éparpillées."], after: "Le Pilotage devient votre écran d'ouverture : chaque véhicule a sa ligne, chaque réservation son bloc, chaque tâche apparaît au bon moment. Vous savez ce qui part, ce qui rentre et ce qui mérite votre attention — en un regard." },
  "assistant-ia": { pains: ["Analyser son activité demande d'ouvrir un tableur, croiser des exports et y passer une soirée.", "Les tendances (véhicules qui dorment, jours qui cartonnent) restent invisibles trop longtemps.", "Personne n'a le temps de vérifier une intuition chiffrée en pleine saison."], after: "Vous posez une question en français, Copilot répond avec vos chiffres réels : véhicules stars, fréquences de réservation, tendances. L'analyse qui prenait une soirée arrive pendant votre café." },
  "page-de-reservation": { pains: ["Sans site, chaque réservation exige 3 à 5 échanges téléphoniques.", "Les clients de 23h ne rappellent jamais le lendemain : ils réservent chez un concurrent.", "Un site vitrine classique impose de maintenir tarifs et véhicules à deux endroits."], after: "Junago génère et héberge votre vrai site de réservation : disponibilités réelles, paiement en ligne, options proposées au bon moment. Le client réserve seul, la réservation tombe dans votre planning — même pendant que vous dormez." },
  "produits-options": { pains: ["Les extras (sièges bébé, franchise, livraison) sont proposés à l'oral… quand on y pense.", "Impossible de savoir combien d'options sont réellement disponibles par agence.", "Le panier moyen stagne alors que la demande existe."], after: "Vos produits et options sont proposés automatiquement, en ligne comme au comptoir, avec un stock maîtrisé par agence. Chaque réservation devient une occasion de vendre plus — sans effort commercial." },
  "reservations-devis": { pains: ["Les demandes prises au téléphone finissent sur des post-it, puis se perdent.", "Un devis envoyé par email n'est jamais relancé au bon moment.", "Transformer un devis accepté en location impose de tout ressaisir."], after: "Toute demande devient une réservation propre en 30 secondes. Le devis accepté se transforme en location d'un clic, le cycle complet est tracé et les relances arrivent toutes seules." },
  "contrats-signature": { pains: ["Chaque départ commence par un document Word recopié à la main.", "Les contrats papier s'égarent, les mentions varient d'une location à l'autre.", "Faire signer un client pressé prend de longues minutes au comptoir."], after: "Le contrat se génère tout seul depuis la réservation, complet et cohérent. Le client signe sur son téléphone, avant même d'arriver — l'exemplaire signé s'archive avec la location." },
  "etats-des-lieux": { pains: ["L'état des lieux papier ne prouve rien : pas de photos, pas d'horodatage.", "Au retour, la discussion sur une rayure devient un bras de fer.", "Les relevés s'entassent dans un classeur, introuvables le jour du litige."], after: "Photos horodatées zone par zone, comparaison automatique départ/retour : les aggravations sont détectées et documentées face au client. Le litige se règle preuves en main, en 30 secondes." },
  "flotte-entretien": { pains: ["Le contrôle technique qui expire en pleine saison, personne ne l'a vu venir.", "Les documents des véhicules dorment dans des pochettes cartonnées.", "Impossible de dire quel véhicule rapporte vraiment, entretien déduit."], after: "Chaque véhicule a sa fiche complète, ses documents suivis et ses alertes d'échéance. La rentabilité réelle se calcule toute seule — vous savez quoi garder, revendre ou racheter." },
  "crm-clients": { pains: ["Les coordonnées clients sont éparpillées entre le téléphone et de vieux contrats.", "Un habitué qui rappelle doit tout redonner, à chaque fois.", "Les prospects sans réponse ne sont jamais relancés."], after: "Chaque client a sa fiche : permis, historique, documents, chiffre d'affaires. L'habitué qui rappelle est reconnu en 3 lettres, le prospect est relancé au bon moment — et converti en 1 clic." },
  "encaissements": { pains: ["Les virements promis arrivent en retard — ou jamais.", "Les espèces et chèques compliquent la comptabilité et la sécurité.", "Une annulation de dernière minute sans acompte, c'est un véhicule immobilisé pour rien."], after: "Vos clients paient par carte via Stripe, en 3D Secure. L'acompte engage la réservation, les fonds arrivent automatiquement sur votre compte, et chaque paiement est réconcilié avec sa location." },
  "revenus-facturation": { pains: ["La facturation de fin de mois mobilise un dimanche entier sur Excel.", "Numérotation, TVA, avoirs : chaque facture manuelle est un risque d'erreur.", "Qui a payé quoi, quand ? Personne ne peut répondre vite."], after: "Chaque encaissement génère sa facture conforme, les avoirs se créent en un clic, les cautions sont tracées. Votre cockpit financier répond en temps réel — et l'export comptable part en un clic." },
  "parametrages": { pains: ["Les logiciels rigides imposent leurs process : c'est vous qui vous adaptez.", "Les informations spécifiques (numéro de vol, lieu de livraison) n'ont nulle part où vivre.", "Chaque équipe bricole ses propres codes couleur sur un tableau blanc."], after: "Statuts colorés, champs personnalisés, seuils d'alerte, catégories réorganisables : Junago épouse votre façon de travailler. Votre process devient celui du logiciel — pas l'inverse." },
  "equipe-securite": { pains: ["Toute l'équipe partage le même compte, avec les mêmes droits.", "Les agents du comptoir voient les marges et les paramétrages.", "Un tarif modifié, une annulation : impossible de savoir qui, quand."], after: "Chaque collaborateur a son compte et son rôle précis, chaque agence son périmètre, chaque action sa trace dans le journal d'activité. Déléguer redevient simple — et sûr." },
  "emails-automatiques": { pains: ["Les mêmes emails de confirmation sont réécrits à la main, réservation après réservation.", "Un rappel oublié, et c'est un no-show au petit matin.", "Personne ne sait ce que le client a réellement reçu."], after: "Confirmation, rappel avant départ, contrat, facture : chaque étape déclenche le bon email, à vos couleurs, avec les bons documents joints. Tout est tracé, vous n'avez rien tapé." },
};

const CHAIN = [
  { label: "Calendrier", icon: CalendarDays, slug: "pilotage-calendrier" },
  { label: "Réservations", icon: ClipboardList, slug: "reservations-devis" },
  { label: "Contrat", icon: FileText, slug: "contrats-signature" },
  { label: "Signature", icon: PenLine, slug: "contrats-signature" },
  { label: "Paiement", icon: CreditCard, slug: "encaissements" },
  { label: "Facturation", icon: Receipt, slug: "revenus-facturation" },
  { label: "Emails", icon: MailCheck, slug: "emails-automatiques" },
  { label: "Copilot", icon: Sparkles, slug: "assistant-ia" },
  { label: "Site internet", icon: Globe, slug: "page-de-reservation" },
];

const SCENARIO = [
  { time: "08h00", title: "Une réservation arrive", desc: "Depuis votre site internet de réservation, acompte payé en ligne." },
  { time: "08h00", title: "Le calendrier se met à jour", desc: "Le véhicule est bloqué sur les bonnes dates, sans double saisie." },
  { time: "08h01", title: "Le contrat est généré", desc: "Pré-rempli, prêt à signer électroniquement." },
  { time: "08h01", title: "Le paiement est suivi", desc: "Acompte encaissé, solde et caution planifiés au départ." },
  { time: "08h02", title: "Copilot prépare les tâches", desc: "Préparation, nettoyage et état des lieux assignés à l'équipe." },
  { time: "08h02", title: "Le client reçoit son email", desc: "Confirmation à vos couleurs, contrat joint, rappel programmé." },
  { time: "08h02", title: "Le site met à jour ses disponibilités", desc: "Le véhicule disparaît des dates réservées, partout, instantanément." },
];

const FAQ = [
  { q: "Cette fonctionnalité est-elle incluse dans l'abonnement ?", a: "Oui. Junago est une plateforme tout-en-un : toutes les fonctionnalités présentées sur ce site sont incluses, sans module payant ni supplément caché." },
  { q: "Puis-je l'essayer avant de m'engager ?", a: "Oui : 30 jours d'essai gratuit, sans carte bancaire, avec l'intégralité de la plateforme. Vous résiliez en 1 clic si Junago ne vous convient pas." },
  { q: "Comment migrer depuis mon outil actuel ?", a: "L'import CSV vous permet de migrer véhicules et clients en quelques minutes. La plupart des loueurs abandonnent leur tableur dans les deux premières semaines." },
  { q: "Et si j'ai besoin d'aide ?", a: "Le centre d'assistance regroupe 24 guides pratiques, et notre équipe vous répond en français 6j/7." },
];

function FaqItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`border rounded-2xl bg-white overflow-hidden transition-all duration-300 ${open ? "border-violet-300 shadow-[0_12px_32px_-16px_rgba(124,58,237,0.3)]" : "border-slate-200"}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left" data-testid={testid}>
        <span className="font-heading text-base font-medium text-slate-900">{q}</span>
        <span className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${open ? "bg-violet-600 rotate-180" : "bg-slate-100"}`}>
          <ChevronDown className={`h-4 w-4 ${open ? "text-white" : "text-slate-500"}`} />
        </span>
      </button>
      <div className={`grid transition-[grid-template-rows,opacity] duration-400 ease-out ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden"><p className="px-6 pb-5 text-sm text-slate-600 leading-relaxed">{a}</p></div>
      </div>
    </div>
  );
}

function Hotspot({ x, y, title, text, i }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="absolute hidden sm:block" style={{ left: x, top: y }}>
      <button
        onClick={() => setOpen(!open)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="h-8 w-8 rounded-full bg-violet-600/90 backdrop-blur border-2 border-white text-white flex items-center justify-center shadow-[0_8px_24px_-6px_rgba(124,58,237,0.8)] transition-transform hover:scale-110"
        style={{ animation: "nodePulse 2.6s ease-in-out infinite", animationDelay: `${i * 0.6}s` }}
        data-testid={`feature-hotspot-${i}`}
      >
        <Plus className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-45" : ""}`} />
      </button>
      <div className={`absolute left-1/2 -translate-x-1/2 bottom-full mb-3 w-56 rounded-xl bg-slate-950/95 backdrop-blur border border-white/10 p-3.5 text-left transition-all duration-300 z-20 ${open ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"}`}>
        <div className="text-[12px] font-bold text-white">{title}</div>
        <p className="mt-1 text-[11px] text-slate-300 leading-relaxed">{text}</p>
      </div>
    </div>
  );
}

const HOTSPOT_POS = [{ x: "18%", y: "28%" }, { x: "62%", y: "20%" }, { x: "44%", y: "62%" }];

export default function FeaturePage() {
  const { slug } = useParams();
  const feature = getFeaturePage(slug);

  useEffect(() => {
    if (feature) document.title = feature.seoTitle;
    window.scrollTo(0, 0);
  }, [feature]);

  if (!feature) return <Navigate to="/fonctionnalites" replace />;

  const idx = FEATURE_PAGES.findIndex((f) => f.slug === slug);
  const prev = FEATURE_PAGES[(idx - 1 + FEATURE_PAGES.length) % FEATURE_PAGES.length];
  const next = FEATURE_PAGES[(idx + 1) % FEATURE_PAGES.length];
  const related = [FEATURE_PAGES[(idx + 1) % FEATURE_PAGES.length], FEATURE_PAGES[(idx + 2) % FEATURE_PAGES.length], FEATURE_PAGES[(idx + 3) % FEATURE_PAGES.length]];
  const story = STORIES[slug];
  const capture = CAPTURES[slug];
  const portrait = PORTRAITS[feature.quote.author];
  const hotspots = feature.benefits.slice(0, 3).map((b, i) => ({ ...HOTSPOT_POS[i], title: b.title, text: b.desc }));

  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />

      {/* ─── Hero épuré centré ─── */}
      <section className="relative pt-10 md:pt-16 pb-10 overflow-hidden" data-testid="feature-hero">
        <div aria-hidden className="absolute top-0 left-1/2 -translate-x-1/2 h-[420px] w-[720px] rounded-full bg-violet-400/10 blur-[110px] pointer-events-none" />
        <div aria-hidden className="absolute top-24 left-[14%] h-2 w-2 rounded-full bg-violet-400/50 hidden md:block" style={{ animation: "orbitFloat 9s ease-in-out infinite" }} />
        <div aria-hidden className="absolute top-48 right-[12%] h-2.5 w-2.5 rounded-full bg-fuchsia-400/40 hidden md:block" style={{ animation: "orbitFloat 7s 1.2s ease-in-out infinite" }} />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <nav className="flex items-center justify-center gap-1.5 text-xs text-slate-400 mb-8" data-testid="feature-breadcrumb">
            <Link to="/" className="hover:text-slate-700">Accueil</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/fonctionnalites" className="hover:text-slate-700">Fonctionnalités</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-slate-700 font-medium">{feature.menuLabel}</span>
          </nav>
          <Reveal>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-violet-50 border border-violet-200 mb-6">
              <feature.icon className="h-3.5 w-3.5 text-violet-600" />
              <span className="text-xs font-bold tracking-[0.15em] uppercase text-violet-700">{feature.overline}</span>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.08] text-slate-900" data-testid="feature-title">
              {feature.title}
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">{feature.subtitle}</p>
          </Reveal>
          <Reveal delay={300}>
            <div className="mt-9 flex flex-col sm:flex-row justify-center gap-3">
              <Link to="/register?context=vendor" className="btn-primary inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold transition-transform duration-300 hover:scale-[1.04] hover:shadow-[0_20px_50px_-15px_rgba(124,58,237,0.6)]" data-testid="feature-cta-start">
                <Gift className="h-4 w-4" /> Commencer gratuitement
              </Link>
              <Link to={`/assistance/article/${feature.helpSlug}`} className="btn-jn-secondary inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold" data-testid="feature-cta-article">
                <BookOpen className="h-4 w-4" /> Lire le guide
              </Link>
            </div>
            <p className="mt-4 text-xs text-slate-500">30 jours d'essai gratuit · Sans carte bancaire · Résiliable en 1 clic</p>
          </Reveal>
        </div>
      </section>

      {/* ─── Grande démonstration + hotspots ─── */}
      <section className="overflow-x-clip"><div className="max-w-6xl mx-auto px-6 pb-20" data-testid="feature-demo">
        <Reveal y={40}>
          <div className="relative">
            <div aria-hidden className="absolute -inset-x-8 -inset-y-6 opacity-40 blur-3xl pointer-events-none" style={{ background: "radial-gradient(ellipse at 30% 20%, rgba(124,58,237,0.3), transparent 55%), radial-gradient(ellipse at 75% 85%, rgba(217,70,239,0.18), transparent 50%)" }} />
            <div className="relative rounded-3xl overflow-hidden ring-1 ring-slate-900/10 shadow-[0_60px_120px_-40px_rgba(15,23,42,0.5)] transition-transform duration-700 hover:scale-[1.01]" data-testid="feature-demo-frame">
              {capture ? (
                <img src={capture} alt={`${feature.menuLabel} — capture réelle du logiciel Junago`} className="w-full block" loading="lazy" />
              ) : (
                <feature.Mockup />
              )}
              {hotspots.map((h, i) => (
                <Hotspot key={i} i={i} x={h.x} y={h.y} title={h.title} text={h.text} />
              ))}
            </div>
            {capture && <p className="mt-4 text-xs text-slate-500 text-center">Capture réelle du logiciel Junago — survolez les points pour explorer.</p>}
          </div>
        </Reveal>
      </div></section>

      {feature.slug === "etats-des-lieux" && <InspectionFilmstrip />}

      {/* ─── Storytelling : le problème / avec Junago ─── */}
      {story && (
        <section className="bg-slate-950 text-white" data-testid="feature-story">
          <div className="max-w-6xl mx-auto px-6 py-16 md:py-24 grid lg:grid-cols-2 gap-12">
            <div>
              <Reveal><div className="text-xs font-bold tracking-[0.2em] uppercase text-red-300/80 mb-4">Avant Junago</div></Reveal>
              <Reveal delay={80}><h2 className="font-heading text-2xl md:text-3xl font-medium tracking-tight leading-snug">Le quotidien, sans le bon outil.</h2></Reveal>
              <div className="mt-7 space-y-3">
                {story.pains.map((p, i) => (
                  <Reveal key={i} delay={150 + i * 120}>
                    <div className="flex items-start gap-3.5 rounded-2xl bg-white/[0.04] border border-white/10 p-5">
                      <XCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                      <p className="text-sm text-slate-300 leading-relaxed">{p}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
            <div className="lg:pt-14">
              <Reveal delay={200}>
                <div className="sheen-card relative rounded-3xl bg-gradient-to-br from-violet-950/80 to-slate-950 border border-violet-500/20 p-8 md:p-10 overflow-hidden">
                  <div aria-hidden className="absolute -right-14 -top-20 h-56 w-56 rounded-full bg-violet-600/30 blur-3xl" />
                  <div className="relative">
                    <div className="text-xs font-bold tracking-[0.2em] uppercase text-violet-300 mb-4">Avec Junago</div>
                    <p className="text-base md:text-lg text-slate-200 leading-relaxed">{story.after}</p>
                    <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-emerald-300">
                      <Check className="h-4 w-4" strokeWidth={3} /> Inclus dans la plateforme, sans supplément
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* ─── Ce que fait cette fonctionnalité ─── */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="feature-benefits">
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-20">
          <Reveal><div className={`${OVERLINE} mb-3`}>Dans le détail</div></Reveal>
          <Reveal delay={80}><h2 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-slate-900">Ce que fait cette fonctionnalité</h2></Reveal>
          <StaggerReveal className="mt-9 grid sm:grid-cols-2 gap-5" step={100} y={26}>
            {feature.benefits.map((b) => (
              <div key={b.title} className="group rounded-2xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:border-violet-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-20px_rgba(124,58,237,0.35)]">
                <div className="h-11 w-11 rounded-xl bg-violet-50 flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-[-6deg] group-hover:bg-violet-600">
                  <b.icon className="h-5 w-5 text-violet-600 transition-colors duration-300 group-hover:text-white" />
                </div>
                <h3 className="mt-4 font-heading text-lg font-medium text-slate-900 tracking-tight group-hover:text-violet-700 transition-colors">{b.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </StaggerReveal>
        </div>
      </section>

      {/* ─── Écosystème : travaille avec tout Junago ─── */}
      <section className="max-w-6xl mx-auto px-6 py-16 md:py-24" data-testid="feature-ecosystem">
        <Reveal><div className={`${OVERLINE} mb-3 text-center`}>Une plateforme, pas un module</div></Reveal>
        <Reveal delay={80}><h2 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-slate-900 text-center">Comment {feature.menuLabel} travaille avec tout Junago</h2></Reveal>
        <Reveal delay={140}><p className="mt-4 text-sm md:text-base text-slate-600 text-center max-w-xl mx-auto">Chaque module alimente les autres : une seule saisie, et toute la plateforme se met en mouvement.</p></Reveal>
        <div className="mt-12 flex flex-wrap justify-center items-center gap-y-5">
          {CHAIN.map((n, i) => {
            const isCurrent = n.slug === slug;
            return (
              <React.Fragment key={n.label + i}>
                <Reveal delay={120 + i * 90} as="span" className="inline-block">
                  <Link
                    to={`/fonctionnalites/${n.slug}`}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 ${
                      isCurrent
                        ? "bg-violet-600 border-violet-500 text-white shadow-[0_14px_34px_-10px_rgba(124,58,237,0.7)]"
                        : "bg-white border-slate-200 text-slate-700 hover:border-violet-300 hover:text-violet-700"
                    }`}
                    style={isCurrent ? { animation: "nodePulse 2.6s ease-in-out infinite" } : undefined}
                    data-testid={`feature-chain-${i}`}
                  >
                    <n.icon className="h-4 w-4" /> {n.label}
                  </Link>
                </Reveal>
                {i < CHAIN.length - 1 && (
                  <span aria-hidden className="mx-1.5 flow-dash hidden sm:block" style={{ animationDelay: `${i * 0.25}s` }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </section>

      {/* ─── Scénario réel : timeline animée ─── */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="feature-scenario">
        <div className="max-w-3xl mx-auto px-6 py-16 md:py-20">
          <Reveal><div className={`${OVERLINE} mb-3`}>Un scénario réel</div></Reveal>
          <Reveal delay={80}><h2 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-slate-900">8h00. Une réservation arrive. Voici ce qui se passe.</h2></Reveal>
          <div className="mt-10 relative">
            <div aria-hidden className="absolute left-[19px] top-2 bottom-2 w-px bg-gradient-to-b from-violet-300 via-violet-400 to-fuchsia-300" />
            <div className="space-y-6">
              {SCENARIO.map((s, i) => (
                <Reveal key={i} delay={100 + i * 130} y={22}>
                  <div className="relative flex items-start gap-5 group" data-testid={`feature-scenario-step-${i}`}>
                    <span className="relative z-10 h-10 w-10 rounded-full bg-white border-2 border-violet-500 text-violet-700 text-[10px] font-bold flex items-center justify-center shrink-0 transition-all duration-300 group-hover:bg-violet-600 group-hover:text-white">
                      {s.time}
                    </span>
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 flex-1 transition-all duration-300 group-hover:border-violet-300 group-hover:shadow-[0_16px_36px_-16px_rgba(124,58,237,0.3)]">
                      <div className="font-heading text-base font-medium text-slate-900">{s.title}</div>
                      <p className="mt-1 text-sm text-slate-600">{s.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Témoignage premium ─── */}
      <section className="max-w-5xl mx-auto px-6 py-16 md:py-24" data-testid="feature-testimonial">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl bg-slate-950 text-white px-8 sm:px-14 py-12 md:py-14">
            <div aria-hidden className="absolute -left-20 -top-32 h-80 w-80 rounded-full bg-violet-600 opacity-20 blur-3xl" />
            <div className="relative grid md:grid-cols-[auto,1fr] gap-8 md:gap-12 items-center">
              {portrait ? (
                <img src={portrait} alt={feature.quote.author} className="h-28 w-28 md:h-36 md:w-36 rounded-2xl object-cover border border-white/15" loading="lazy" />
              ) : (
                <div className="h-28 w-28 md:h-36 md:w-36 rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center font-heading text-4xl font-semibold text-white border border-white/15">
                  {feature.quote.author.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                </div>
              )}
              <div>
                <Quote className="h-7 w-7 text-violet-400" />
                <blockquote className="mt-4 font-heading text-xl md:text-2xl font-medium tracking-tight leading-snug">
                  «&nbsp;{feature.quote.text}&nbsp;»
                </blockquote>
                <div className="mt-4 text-sm">
                  <span className="font-semibold">{feature.quote.author}</span>
                  <span className="text-slate-400"> — {feature.quote.role}</span>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ─── FAQ ─── */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="feature-faq">
        <div className="max-w-3xl mx-auto px-6 py-16 md:py-20">
          <Reveal><div className={`${OVERLINE} mb-3 text-center`}>Questions fréquentes</div></Reveal>
          <Reveal delay={80}><h2 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-slate-900 text-center">Avant de vous lancer</h2></Reveal>
          <div className="mt-8 space-y-3">
            {FAQ.map((f, i) => (
              <Reveal key={f.q} delay={100 + i * 90}><FaqItem q={f.q} a={f.a} testid={`feature-faq-${i}`} /></Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Fonctionnalités complémentaires ─── */}
      <section className="max-w-6xl mx-auto px-6 py-16 md:py-20" data-testid="feature-related">
        <Reveal><div className={`${OVERLINE} mb-3`}>Elles travaillent ensemble</div></Reveal>
        <Reveal delay={80}><h2 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-slate-900">Fonctionnalités complémentaires</h2></Reveal>
        <Reveal delay={140}><p className="mt-3 text-sm md:text-base text-slate-600 max-w-xl">Chaque module de Junago amplifie les autres. Voici ceux qui complètent naturellement {feature.menuLabel.toLowerCase()}.</p></Reveal>
        <StaggerReveal className="mt-9 grid sm:grid-cols-3 gap-5" step={110} y={26}>
          {related.map((f) => (
            <Link
              key={f.slug}
              to={`/fonctionnalites/${f.slug}`}
              className="group rounded-2xl border border-slate-200 bg-white overflow-hidden transition-all duration-300 hover:border-violet-300 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_-20px_rgba(124,58,237,0.4)]"
              data-testid={`feature-related-${f.slug}`}
            >
              <div className="relative h-28 bg-gradient-to-br from-violet-600 via-violet-800 to-slate-950 flex items-center justify-center overflow-hidden">
                <div aria-hidden className="absolute -right-8 -bottom-10 h-32 w-32 rounded-full bg-fuchsia-500/30 blur-2xl" />
                <f.icon className="relative h-10 w-10 text-white/85 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-[-6deg]" strokeWidth={1.5} />
              </div>
              <div className="p-6">
                <div className="text-[11px] font-bold tracking-[0.15em] uppercase text-violet-600">{f.overline}</div>
                <div className="mt-2 font-heading text-base font-medium text-slate-900 leading-snug group-hover:text-violet-700 transition-colors">{f.title}</div>
                <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-600">
                  Découvrir <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          ))}
        </StaggerReveal>
        <div className="mt-8 flex items-center justify-between text-sm">
          <Link to={`/fonctionnalites/${prev.slug}`} className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium" data-testid="feature-prev">
            <ArrowLeft className="h-4 w-4" /> {prev.menuLabel}
          </Link>
          <Link to={`/fonctionnalites/${next.slug}`} className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium" data-testid="feature-next">
            {next.menuLabel} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* ─── CTA final renforcé ─── */}
      <section className="max-w-5xl mx-auto px-6 pb-16 md:pb-20" data-testid="feature-cta-bottom">
        <Reveal>
          <div className="sheen-card relative overflow-hidden rounded-3xl bg-slate-950 text-white px-8 sm:px-14 py-14 text-center">
            <div aria-hidden className="absolute -right-20 -bottom-32 h-96 w-96 rounded-full bg-violet-600 opacity-30 blur-3xl" />
            <div aria-hidden className="absolute -left-24 -top-28 h-72 w-72 rounded-full bg-fuchsia-600 opacity-15 blur-3xl" />
            <div className="relative">
              <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight">
                Testez {feature.menuLabel.toLowerCase()} dès aujourd'hui.
              </h2>
              <p className="mt-3 text-slate-400">30 jours d'essai complet, sans carte bancaire.</p>
              <Link
                to="/register?context=vendor"
                className="btn-jn-light mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold transition-transform duration-300 hover:scale-[1.04]"
                data-testid="feature-bottom-start"
              >
                Commencer gratuitement <ArrowRight className="btn-arrow h-4 w-4" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}
